import useStyles from "../styles";
import {
  Grid,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import NoData from "../../dashboard/components/NotFoundCard/NoData";
import NoAccount from "../../../assets/images/emptyacct.png";
import { Icon } from "@iconify/react/dist/iconify.js";

import currencyCodes from "currency-codes";
import getSymbolFromCurrency from "currency-symbol-map";

const BankingInfo = ({ data }) => {
  const classes = useStyles();
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  const bankAccounts = data?.bank_details || [];

  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return "N/A";
    const symbol = getSymbolFromCurrency(currencyCode);

    return symbol || currencyCode;
  };

  useEffect(() => {
    const loadCurrencyOptions = () => {
      try {
        const currencies = currencyCodes.data.map(currency => ({
          value: currency.code,
          label: `${currency.currency} (${currency.code})`
        }));

        currencies.sort((a, b) => a.label.localeCompare(b.label));

        setCurrencyOptions(currencies);
      } catch (error) {
        setCurrencyOptions([
          { value: "USD", label: "US Dollar (USD)" },
          { value: "EUR", label: "Euro (EUR)" },
          { value: "GBP", label: "British Pound (GBP)" },
          { value: "NGN", label: "Nigerian Naira (NGN)" },
          { value: "CAD", label: "Canadian Dollar (CAD)" },
          { value: "AUD", label: "Australian Dollar (AUD)" }
        ]);
      }
    };

    loadCurrencyOptions();
  }, []);

  const getCurrencyName = (currencyCode) => {
    if (!currencyCode) return "N/A";

    const currency = currencyOptions.find(option => option.value === currencyCode);
    if (currency) {
      return currency.label.split(" (")[0];
    }

    const commonCurrencies = {
      "USD": "US Dollar",
      "EUR": "Euro",
      "GBP": "British Pound",
      "NGN": "Nigerian Naira",
      "CAD": "Canadian Dollar",
      "AUD": "Australian Dollar"
    };

    return commonCurrencies[currencyCode] || currencyCode;
  };

  const handleAccordionChange = (index) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? index : false);
  };

  const copyAccountDetails = (bank) => {
    const accountDetails = `Bank Name: ${bank.bank_name}\nAccount Number: ${bank.account_number}\nAccount Name: ${bank.account_name}`;

    navigator.clipboard.writeText(accountDetails)
      .then(() => {
        toast.success("Account details copied to clipboard!");
      })
      .catch(() => {
        const textArea = document.createElement("textarea");
        textArea.value = accountDetails;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success("Account details copied to clipboard!");
      });
  };

  const copyIndividualDetail = (text, label) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast.success(`${label} copied to clipboard!`);
      })
      .catch(() => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success(`${label} copied to clipboard!`);
      });
  };

  return (
    <Grid container spacing={4}>
      <Grid item lg={3} md={4} sm={12} xs={12}>
        <div className="left-container">
          <h4>Banking Information</h4>
          <h6>See your Organisation Banking Information</h6>
        </div>
      </Grid>

      <Grid item lg={9} md={8} sm={12} xs={12}>
        <div className="right-container">
          {bankAccounts.length > 0 ? (
            <div>
              {bankAccounts.map((bank, index) => (
                <Accordion
                  key={index}
                  expanded={expandedAccordion === index}
                  onChange={handleAccordionChange(index)}
                  sx={{
                    marginBottom: "10px",
                    boxShadow: "none",
                    padding: 0,
                    "&:before": { display: "none" }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<Icon icon="ep:arrow-down-bold" width="24" height="24" />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                    style={{ backgroundColor: "#F0F2F5", borderRadius: "8px" }}
                  >
                    <strong>Account Info {index + 1}</strong>
                  </AccordionSummary>

                  <AccordionDetails sx={{ padding: 0, marginTop: "10px" }}>
                    <div
                      className={`${classes.accord_body} ${bank.primary ? classes.accord_body_primary : ""}`}
                    >
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: "1px solid #E4E7EC",
                        paddingBottom: "10px"
                      }}
                      >
                        <div style={{ display: "flex" }}>
                          <p className={classes.bank_currency}>
                            <strong>{getCurrencySymbol(bank.currency)}</strong>
                          </p>
                          <span><strong>{getCurrencyName(bank.currency)}</strong></span>
                        </div>

                        <Switch
                          checked={bank.status === "active"}
                          color="primary"
                          disabled
                        />
                      </div>

                      <Grid container spacing={2} sx={{ marginTop: "5px" }}>
                        <Grid item lg={6} md={6} sm={6} xs={12}>
                          <p className={classes.account_details}>
                            <strong>Bank Name:</strong>
                            <span className={classes.span}>
                              {bank.bank_name}
                              <Icon
                                icon="ion:copy-outline"
                                width="16"
                                height="16"
                                style={{
                                  marginLeft: "8px",
                                  cursor: "pointer",
                                  verticalAlign: "middle"
                                }}
                                onClick={() => copyIndividualDetail(bank.bank_name, "Bank name")}
                              />
                            </span>
                          </p>
                        </Grid>
                        <Grid item lg={6} md={6} sm={6} xs={12}>
                          <p className={classes.account_details}>
                            <strong>Account Number:</strong>
                            <span className={classes.span}>
                              {bank.account_number}
                              <Icon
                                icon="ion:copy-outline"
                                width="16"
                                height="16"
                                style={{
                                  marginLeft: "8px",
                                  cursor: "pointer",
                                  verticalAlign: "middle"
                                }}
                                onClick={() => copyIndividualDetail(bank.account_number, "Account number")}
                              />
                            </span>
                          </p>
                        </Grid>
                        <Grid item lg={6} md={6} sm={6} xs={12}>
                          <p className={classes.account_details}>
                            <strong>Account Name:</strong>
                            <span className={classes.span}>
                              {bank.account_name}
                              <Icon
                                icon="ion:copy-outline"
                                width="16"
                                height="16"
                                style={{
                                  marginLeft: "8px",
                                  cursor: "pointer",
                                  verticalAlign: "middle"
                                }}
                                onClick={() => copyIndividualDetail(bank.account_name, "Account name")}
                              />
                            </span>
                          </p>
                        </Grid>
                        <Grid item lg={6} md={6} sm={6} xs={12}>
                          <p className={classes.account_details}>
                            <strong>Sort Code:</strong>
                            <span className={classes.span}>
                              {bank.sort_code || "N/A"}
                              {bank.sort_code && (
                                <Icon
                                  icon="ion:copy-outline"
                                  width="16"
                                  height="16"
                                  style={{
                                    marginLeft: "8px",
                                    cursor: "pointer",
                                    verticalAlign: "middle"
                                  }}
                                  onClick={() => copyIndividualDetail(bank.sort_code, "Sort code")}
                                />
                              )}
                            </span>
                          </p>
                        </Grid>
                        <Grid item lg={6} md={6} sm={6} xs={12}>
                          <p className={classes.account_details}>
                            <strong>Routing Number:</strong>
                            <span className={classes.span}>
                              {bank.routing_number || "N/A"}
                              {bank.routing_number && (
                                <Icon
                                  icon="ion:copy-outline"
                                  width="16"
                                  height="16"
                                  style={{
                                    marginLeft: "8px",
                                    cursor: "pointer",
                                    verticalAlign: "middle"
                                  }}
                                  onClick={() => copyIndividualDetail(bank.routing_number, "Routing number")}
                                />
                              )}
                            </span>
                          </p>
                        </Grid>
                        <Grid item lg={6} md={6} sm={6} xs={12}>
                          {bank.primary && (
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              borderRadius: "12px"
                            }}
                            >
                              {[...Array(3)].map((_, starIndex) => (
                                <Icon
                                  key={starIndex}
                                  icon="tabler:star"
                                  width="20"
                                  height="20"
                                  className={classes.Icon}
                                />
                              ))}
                            </div>
                          )}
                        </Grid>
                      </Grid>
                    </div>
                    <div
                      className={classes.Icon}
                      style={{
                        cursor: "pointer",
                        marginTop: "20px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      onClick={() => copyAccountDetails(bank)}
                    >
                      <Icon icon="ion:copy-outline" width="20" height="20" />
                      Copy School Account Details
                    </div>
                  </AccordionDetails>
                </Accordion>
              ))}

            </div>
          ) : (
            <div className={classes.empty}>
              <div>
                <NoData
                  img={NoAccount}
                  title="No Bank Added"
                  subtitle="There has been no bank account added for payments yet."
                />
              </div>
            </div>
          )}
        </div>
      </Grid>
      {/* <ToastContainer /> */}
    </Grid>
  );
};

export default BankingInfo;
